import React from 'react';
import { motion } from 'framer-motion';
import { getStats, StatsResponse } from '../services/api';

// HeroStats — compact live-stats strip embedded inside the hero. Site-wide
// counters distilled to one line so they land above the fold without
// competing with the headline. Renders as a subtle mono-typed strip beneath
// the CTA buttons. Silently hides on failure or empty data so a broken
// /stats endpoint never leaves debris on the marketing page.
const HeroStats: React.FC = () => {
  const [stats, setStats] = React.useState<StatsResponse | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getStats();
        if (!cancelled) setStats(s);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (failed || !stats) return null;
  // Don't paint an empty strip while the updater hasn't ticked yet.
  const empty = stats.stacksCompleted === 0 && stats.lightFrames === 0 && stats.integrationSeconds === 0;
  if (empty) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="mt-8 inline-flex items-center gap-3 md:gap-4 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur text-[11px] md:text-xs font-mono tabular-nums text-white/70"
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-aurora-400 opacity-70 animate-ping" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-aurora-400" />
      </span>
      <span className="uppercase tracking-widest text-white/45 hidden sm:inline">Since first light</span>
      <StripItem value={stats.stacksCompleted.toLocaleString()} label="stacks" />
      <Sep />
      <StripItem value={formatHours(stats.integrationSeconds)} label="shutter" />
      <Sep />
      <StripItem value={stats.lightFrames.toLocaleString()} label="frames" />
      <Sep />
      <StripItem value={stats.astronomers.toLocaleString()} label={stats.astronomers === 1 ? 'astronomer' : 'astronomers'} />
      {stats.anonymousShares > 0 && (
        <>
          <Sep />
          <StripItem value={stats.anonymousShares.toLocaleString()} label={stats.anonymousShares === 1 ? 'preview' : 'previews'} />
        </>
      )}
    </motion.div>
  );
};

const StripItem: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <span className="flex items-baseline gap-1">
    <span className="text-white font-semibold">{value}</span>
    <span className="text-white/45">{label}</span>
  </span>
);

const Sep: React.FC = () => (
  <span className="text-white/20">·</span>
);

// Match the full widget's hoursFmt but drop the leading-zero minute padding
// on a single-line strip — "89h 4m" reads cleaner than "89h 04m" at this size.
const formatHours = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h.toLocaleString()}h ${m}m`;
};

export default HeroStats;
